import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApi, useDirectory, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { projectFields } from "@/lib/schemas";
import { fmtDate, money, optionList, PROJECT_STATUSES, PROJECT_TYPES, todayISO } from "@/lib/format";

export default function Projects() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [q, setQ] = useState("");
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/projects", { q, ...f });
  const { data: templates } = useApi("/templates");
  const { options: staff, name } = useDirectory();
  const clients = useClientOptions();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div>
      <PageHeader title="Proyectos" subtitle={isAdmin ? "Todos los proyectos." : "Proyectos asignados a usted o a sus clientes."}
        actions={isAdmin && <Button onClick={() => setOpen(true)} data-testid="new-project-button"><Plus className="mr-2 h-4 w-4" />Nuevo proyecto</Button>} />
      <FilterBar search={q} onSearch={setQ} values={f} onChange={setF} testid="projects-filters" filters={[
        { key: "status", label: "Estado", options: optionList(PROJECT_STATUSES) }, { key: "type", label: "Tipo", options: optionList(PROJECT_TYPES) },
        { key: "client_id", label: "Cliente", options: clients }, { key: "responsible_id", label: "Responsable", options: staff }]} />
      <ErrorBox text={error} />
      <DataTable testid="projects-table" quickView={(r) => r.name} loading={loading} rows={data} onRowClick={(r) => navigate(`/proyectos/${r.id}`)} empty="No hay proyectos."
        columns={[
          { key: "name", label: "Proyecto", render: (r) => <span className="font-medium text-slate-900">{r.name}<span className="block text-xs font-normal text-slate-500">{r.client_name} · {r.type}</span></span> },
          { key: "responsible_id", label: "Responsable", render: (r) => name(r.responsible_id) },
          { key: "progress", label: "Avance", render: (r) => <div className="flex w-32 items-center gap-2"><Progress value={r.progress || 0} className="h-2" /><span className="num text-xs">{r.progress || 0}%</span></div> },
          { key: "estimated_delivery", label: "Entrega", render: (r) => <span className={r.delayed ? "font-semibold text-rose-600" : ""}>{fmtDate(r.estimated_delivery)}{r.delayed && " · Retrasado"}</span> },
          { key: "price_sold", label: "Precio vendido", render: (r) => <span className="num text-xs">{money(r.price_sold)}</span> },
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      <FormDialog open={open} onOpenChange={setOpen} title="Nuevo proyecto" testid="project-form" wide
        fields={[...projectFields(staff, clients, true), { name: "template_id", label: "Aplicar plantilla de tareas", type: "select", placeholder: "Sin plantilla", options: (templates || []).map((t) => ({ value: t.id, label: t.name })) }]}
        initial={{ status: "Nuevo", type: "Página web", priority: "Normal", start_date: todayISO(), progress: 0 }}
        onSubmit={async ({ template_id, ...v }) => {
          const r = await api.post("/projects", v, { params: template_id ? { template_id } : {} });
          toast.success("Proyecto creado"); reload(); navigate(`/proyectos/${r.data.id}`);
        }} />
    </div>
  );
}
